import React, { useEffect, useRef, useState } from "react";
import { Monitor, Download, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";
import VoxaraScribe from "@/components/shared/VoxaraScribe";

// The landing shows the same mascot the recorder shows, driven by a synthetic signal instead of a
// microphone. Reusing the component rather than redrawing it means the promise on the marketing
// page and the thing the customer sees while recording can never drift apart.

const LINES = [
  { speaker: 1, es: "Buenos días, damos inicio a la sesión.", en: "Good morning, we are starting the session." },
  { speaker: 2, es: "Gracias. Procedo con el primer punto.", en: "Thank you. I will move to the first item." },
  { speaker: 1, es: "Adelante, quedamos atentos.", en: "Go ahead, we are listening." },
];

const two = (n) => String(n).padStart(2, "0");

export default function LandingLiveRecording() {
  const { language } = useLanguage();
  const t = (es, en) => (language === "es" ? es : en);

  const levelRef = useRef(0);
  const rafRef = useRef(null);
  const [clock, setClock] = useState("00:00");
  const [visibleLines, setVisibleLines] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      levelRef.current = 0.5;
      setVisibleLines(LINES.length);
      setClock("02:14");
      return undefined;
    }

    const started = performance.now();
    let nextLine = 2.2;
    let shown = 0;
    let lastClock = -1;

    const frame = (ts) => {
      const seconds = (ts - started) / 1000;

      // Two detuned sines read as speech: bursts with gaps, never a steady tone. The floor keeps
      // the pen moving through the quiet parts so the mascot never looks frozen.
      levelRef.current = Math.min(
        1,
        Math.abs(Math.sin(seconds * 3.1) * Math.sin(seconds * 1.27)) * 1.5 + 0.15,
      );

      const whole = Math.floor(seconds) % 3600;
      if (whole !== lastClock) {
        lastClock = whole;
        setClock(`${two(Math.floor(whole / 60))}:${two(whole % 60)}`);
      }

      if (seconds > nextLine) {
        if (shown < LINES.length) {
          shown += 1;
          setVisibleLines(shown);
          nextLine = seconds + 2.2;
        } else {
          shown = 0;
          setVisibleLines(0);
          nextLine = seconds + 1.2;
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [reduced]);

  const chips = [
    { icon: Monitor, label: t("Chrome o Edge, desde computador", "Chrome or Edge, on a computer") },
    { icon: Download, label: t("Te llevas el MP3", "You keep the MP3") },
    { icon: Lock, label: t("El audio se borra al terminar", "The audio is deleted when it finishes") },
  ];

  return (
    <section id="live-recording" className="py-20 px-6" data-testid="landing-live-recording">
      <div className="container mx-auto max-w-6xl">
        <span className="inline-flex rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
          {t("Nuevo", "New")}
        </span>

        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
          {t("Graba en vivo, ", "Record live, ")}
          <span className="accent-text">{t("sin instalar nada", "nothing to install")}</span>
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground leading-relaxed">
          {t("Reuniones, entrevistas, clases o audiencias. Voxara capta el audio de tu computador y te devuelve la transcripción.",
             "Meetings, interviews, lectures or hearings. Voxara captures your computer's audio and gives you back the transcript.")}
        </p>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Card className="bg-card border-border p-4 card-hover">
            <div className="flex items-center gap-2 px-2 pb-1">
              <span className={`h-2 w-2 rounded-full bg-primary ${reduced ? "" : "animate-pulse"}`} />
              <span className="text-xs text-foreground">{t("Escuchando y transcribiendo", "Listening and transcribing")}</span>
              <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid="landing-live-clock">
                {clock}
              </span>
            </div>
            {/* The mascot caps at 500px, so on a stacked tablet card it would sit off to one side. */}
            <div className="mx-auto w-full max-w-[500px]">
              <VoxaraScribe levelRef={levelRef} active />
            </div>
          </Card>

          <Card className="bg-card border-border p-6 card-hover">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {t("Lo que recibes", "What you get")}
            </span>
            <div className="mt-4 flex flex-col gap-3" data-testid="landing-live-transcript">
              {LINES.slice(0, visibleLines).map((line, index) => (
                <div key={index} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <p className="text-[11px] text-primary">
                    {t("Hablante", "Speaker")} {line.speaker}
                  </p>
                  <p className="text-sm leading-relaxed text-foreground">{t(line.es, line.en)}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <span key={chip.label}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground">
              <chip.icon className="h-4 w-4 text-secondary" />
              {chip.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
